import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IndividualMemberDto, WorkSector, Study } from '../../types/member';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { hexToRgba } from '../../utils/colorUtils';

export interface ProfessionalProfileDisplayProps {
  member: IndividualMemberDto;
  className?: string;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const GroupHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding-bottom: ${spacing[3]};
  border-bottom: ${borders.width.thin} solid ${props => props.theme.colors.borderLight || props.theme.colors.border};
`;

const GroupTitle = styled.h3`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
  line-height: 1.3;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]} ${spacing[5]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[4]};
  }
`;

const Field = styled.div<{ $span?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 0;
  ${props =>
    props.$span &&
    `
    grid-column: 1 / -1;
  `}
`;

const FieldLabel = styled(Text)`
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-size: ${typography.fontSize.xs};
`;

const FieldValue = styled(Text)`
  word-break: break-word;
`;

const EmptyHint = styled(Text)`
  font-style: italic;
  color: ${props => props.theme.colors.textMuted};
`;

const BilingualStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BilingualLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing[2]};
  min-width: 0;
`;

const LangTag = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
  opacity: 0.85;
  line-height: 1.4;
`;

const StudiesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const StudyItem = styled.li`
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borders.radius.md};
  background: ${props => props.theme.colors.surfaceHover || props.theme.colors.icomWarmGray};
  border-left: 3px solid ${props => props.theme.colors.primary};
`;

const StudyTitle = styled(Text)`
  display: block;
`;

const StudyMeta = styled(Text)`
  display: block;
  margin-top: ${spacing[1]};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

const StatusChip = styled.span<{ $tone: 'primary' | 'accent' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: ${borders.radius.md};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  line-height: 1.3;
  background: ${props => {
    if (props.$tone === 'primary') return hexToRgba(props.theme.colors.primary, 0.1);
    if (props.$tone === 'accent') return hexToRgba(props.theme.colors.accent, 0.14);
    return props.theme.colors.surfaceHover;
  }};
  color: ${props => {
    if (props.$tone === 'primary') return props.theme.colors.primary;
    if (props.$tone === 'accent') return props.theme.colors.accentDark || props.theme.colors.accent;
    return props.theme.colors.textSecondary;
  }};
  border: ${borders.width.thin} solid
    ${props => {
      if (props.$tone === 'primary') return hexToRgba(props.theme.colors.primary, 0.22);
      if (props.$tone === 'accent') return hexToRgba(props.theme.colors.accent, 0.28);
      return props.theme.colors.border;
    }};
`;

function preferGreek(lang: string): boolean {
  return lang.startsWith('el') || lang === 'gr';
}

function pickText(en?: string | null, gr?: string | null, preferGr?: boolean): string | null {
  const primary = preferGr ? gr || en : en || gr;
  return primary?.trim() || null;
}

function hasText(value?: string | null): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

const BilingualValue: React.FC<{
  en?: string | null;
  gr?: string | null;
  preferGr: boolean;
  fallback?: string | null;
}> = ({ en, gr, preferGr, fallback }) => {
  const enVal = hasText(en) ? en.trim() : null;
  const grVal = hasText(gr) ? gr.trim() : null;
  const fallbackVal = hasText(fallback) ? fallback.trim() : null;

  if (!enVal && !grVal) {
    if (!fallbackVal) return null;
    return (
      <FieldValue size="base" weight="medium" color="text">
        {fallbackVal}
      </FieldValue>
    );
  }

  if (enVal && grVal && enVal === grVal) {
    return (
      <FieldValue size="base" weight="medium" color="text">
        {enVal}
      </FieldValue>
    );
  }

  if (enVal && grVal) {
    const primary = preferGr ? grVal : enVal;
    const secondary = preferGr ? enVal : grVal;
    const primaryLang = preferGr ? 'EL' : 'EN';
    const secondaryLang = preferGr ? 'EN' : 'EL';
    return (
      <BilingualStack>
        <BilingualLine>
          <LangTag aria-hidden>{primaryLang}</LangTag>
          <FieldValue size="base" weight="medium" color="text">
            {primary}
          </FieldValue>
        </BilingualLine>
        <BilingualLine>
          <LangTag aria-hidden>{secondaryLang}</LangTag>
          <FieldValue size="sm" color="secondary">
            {secondary}
          </FieldValue>
        </BilingualLine>
      </BilingualStack>
    );
  }

  const single = enVal || grVal;
  return (
    <FieldValue size="base" weight="medium" color="text">
      {single}
    </FieldValue>
  );
};

const ProfileField: React.FC<{
  label: string;
  children: React.ReactNode;
  span?: boolean;
  hideWhenEmpty?: boolean;
}> = ({ label, children, span, hideWhenEmpty = true }) => {
  if (hideWhenEmpty && (children === null || children === undefined || children === false)) {
    return null;
  }
  return (
    <Field $span={span}>
      <FieldLabel size="xs" color="secondary">
        {label}
      </FieldLabel>
      {children}
    </Field>
  );
};

function formatStudy(
  study: Study,
  preferGr: boolean
): { title: string; institution: string | null } {
  const title =
    pickText(study.titleEn, study.titleGr, preferGr) ||
    study.titleEn ||
    study.titleGr ||
    '';
  const institution = pickText(
    study.institutionEn ?? study.institution,
    study.institutionGr,
    preferGr
  );
  return { title, institution };
}

export const ProfessionalProfileDisplay: React.FC<ProfessionalProfileDisplayProps> = ({
  member,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const preferGr = preferGreek(i18n.language);

  const getWorkSectorLabel = (sector?: WorkSector): string | null => {
    if (sector == null) return null;
    const labels: Partial<Record<WorkSector, string>> = {
      [WorkSector.Museology]: t('workSector.museology'),
      [WorkSector.Education]: t('workSector.education'),
      [WorkSector.Documentation]: t('workSector.documentation'),
      [WorkSector.Conservation]: t('workSector.conservation'),
      [WorkSector.Exhibition]: t('workSector.exhibition'),
      [WorkSector.Management]: t('workSector.management'),
      [WorkSector.Communication]: t('workSector.communication'),
      [WorkSector.Other]: t('workSector.other'),
    };
    return labels[sector] ?? null;
  };

  const titleEn = member.professionalTitle?.en;
  const titleGr = member.professionalTitle?.gr;
  const hasTitle = hasText(titleEn) || hasText(titleGr);

  const institutionEn = member.professionalInstitutionName?.en;
  const institutionGr = member.professionalInstitutionName?.gr;
  const hasInstitution = hasText(institutionEn) || hasText(institutionGr);

  const sectorLabel = getWorkSectorLabel(member.workSector);
  const postal = hasText(member.professionalPostalCode) ? member.professionalPostalCode.trim() : null;
  const email = hasText(member.professionalEmail) ? member.professionalEmail.trim() : null;
  const phone = hasText(member.professionalPhone) ? member.professionalPhone.trim() : null;

  const addressEn = member.professionalAddressEn;
  const addressGr = member.professionalAddressGr;
  const addressFallback =
    !hasText(addressEn) && !hasText(addressGr) ? member.professionalAddress : null;

  const cityEn = member.professionalCityEn;
  const cityGr = member.professionalCityGr;
  const cityFallback = !hasText(cityEn) && !hasText(cityGr) ? member.professionalCity : null;

  const hasAddress = hasText(addressEn) || hasText(addressGr) || hasText(addressFallback);
  const hasCity = hasText(cityEn) || hasText(cityGr) || hasText(cityFallback);

  const workFields = [
    hasTitle,
    sectorLabel,
    hasInstitution,
    hasAddress,
    hasCity,
    postal,
    email,
    phone,
  ].some(Boolean);

  const studies = member.studies ?? [];
  const hasStudies = studies.length > 0;

  const statusFlags: { key: string; label: string; tone: 'primary' | 'accent' | 'neutral' }[] = [];
  if (member.isGraduateStudent) {
    statusFlags.push({
      key: 'student',
      label: t('profile.isGraduateStudent'),
      tone: 'primary',
    });
  }
  if (member.isRetired) {
    statusFlags.push({
      key: 'retired',
      label: t('profile.isRetired'),
      tone: 'accent',
    });
  }
  return (
    <Root className={className}>
      <Group aria-labelledby="profile-work-heading">
        <GroupHeader>
          <GroupTitle id="profile-work-heading">{t('profile.sections.work')}</GroupTitle>
        </GroupHeader>
        {workFields ? (
          <FieldGrid>
            <ProfileField label={t('profile.professionalTitle')}>
              {hasTitle ? (
                <BilingualValue en={titleEn} gr={titleGr} preferGr={preferGr} />
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.workSector')}>
              {sectorLabel ? (
                <FieldValue size="base" weight="medium" color="text">
                  {sectorLabel}
                </FieldValue>
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalInstitution')} span>
              {hasInstitution ? (
                <BilingualValue en={institutionEn} gr={institutionGr} preferGr={preferGr} />
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalAddress')} span>
              {hasAddress ? (
                <BilingualValue
                  en={addressEn}
                  gr={addressGr}
                  preferGr={preferGr}
                  fallback={addressFallback}
                />
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalCity')}>
              {hasCity ? (
                <BilingualValue
                  en={cityEn}
                  gr={cityGr}
                  preferGr={preferGr}
                  fallback={cityFallback}
                />
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalPostalCode')}>
              {postal ? (
                <FieldValue size="base" weight="medium" color="text">
                  {postal}
                </FieldValue>
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalEmail')}>
              {email ? (
                <FieldValue size="base" weight="medium" color="text">
                  {email}
                </FieldValue>
              ) : null}
            </ProfileField>
            <ProfileField label={t('profile.professionalPhone')}>
              {phone ? (
                <FieldValue size="base" weight="medium" color="text">
                  {phone}
                </FieldValue>
              ) : null}
            </ProfileField>
          </FieldGrid>
        ) : (
          <EmptyHint size="sm">{t('profile.sectionEmpty')}</EmptyHint>
        )}
      </Group>

      <Group aria-labelledby="profile-studies-heading">
        <GroupHeader>
          <GroupTitle id="profile-studies-heading">{t('profile.sections.studies')}</GroupTitle>
        </GroupHeader>
        {hasStudies ? (
          <StudiesList>
            {studies.map(study => {
              const { title: studyTitle, institution: studyInst } = formatStudy(study, preferGr);
              return (
                <StudyItem key={study.id}>
                  <StudyTitle size="base" weight="semibold" color="text">
                    {studyTitle}
                  </StudyTitle>
                  {studyInst && (
                    <StudyMeta size="sm" color="secondary">
                      {studyInst}
                    </StudyMeta>
                  )}
                </StudyItem>
              );
            })}
          </StudiesList>
        ) : (
          <EmptyHint size="sm">{t('profile.noStudies')}</EmptyHint>
        )}
      </Group>

      <Group aria-labelledby="profile-status-heading">
        <GroupHeader>
          <GroupTitle id="profile-status-heading">{t('profile.statusFlags')}</GroupTitle>
        </GroupHeader>
        {statusFlags.length > 0 ? (
          <ChipRow role="list">
            {statusFlags.map(flag => (
              <StatusChip key={flag.key} $tone={flag.tone} role="listitem">
                {flag.label}
              </StatusChip>
            ))}
          </ChipRow>
        ) : (
          <EmptyHint size="sm">{t('profile.noStatusFlags')}</EmptyHint>
        )}
      </Group>
    </Root>
  );
};

export default ProfessionalProfileDisplay;
